/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {
  call,
  put,
  select,
  takeLatest,
  takeEvery,
  ForkEffect,
} from "redux-saga/effects";
import * as actions from "#/infra/redux/actionType";
import fetchServiceTokensApi, { ServiceTokenType } from "#/apis/infra/getServiceTokens";
import fetchStatisticsApi, { StatisticsType } from "#/apis/infra/getStatistics";
import { FetchStatisticsPayload } from "#/infra/redux/actionCreator";
import InternalResponse from "#/types/internalResponse";
import { RootState } from "#/types/RootState";

export function* startAxios() {
  try {
    yield put({
      type: actions.SENDING_AXIOS,
    });
  } catch (error) {
    yield put({
      type: actions.INTERNAL_ERROR,
      data: error,
    });
  }
}

/**
 * Timezone fallback: action payload → license API → browser
 */
export function* resolveTimezone(override?: string) {
  if (override) return override;
  try {
    const licenseTimezone: string | undefined = yield select(
      (state: RootState) => state.dashboard?.license?.results?.license_info?.timezone,
    );
    return licenseTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
}

export function* setAxiosResult(data: InternalResponse<unknown>) {
  try {
    yield put({
      type: actions.SET_AXIOS_RESULT,
      data,
    });
  } catch (error) {
    yield put({
      type: actions.INTERNAL_ERROR,
      data: error,
    });
  }
}

function* handleFetchServiceTokens({ data }: { type: string; data: string }) {
  try {
    // Skip if already loaded
    const existing: ServiceTokenType[] | null = yield select(
      (state: RootState) => state.infra?.serviceTokens?.results,
    );
    if (existing && existing.length > 0) {
      yield put({ type: actions.FETCHED_SERVICE_TOKENS, data: existing });
      return;
    }

    const response: InternalResponse<ServiceTokenType[]> = yield call(fetchServiceTokensApi, {
      token: data,
    });

    // eslint-disable-next-line no-nested-ternary
    const tokens = response.success && response.data
      ? Array.isArray(response.data) ? response.data : [response.data]
      : [];

    yield put({ type: actions.FETCHED_SERVICE_TOKENS, data: tokens });
  } catch (error) {
    yield put({ type: actions.FETCHED_SERVICE_TOKENS, data: null });
  }
}

const RANGE_DAYS: Record<string, number> = {
  "7d": 7,
  "14d": 14,
  "30d": 30,
};

function* handleFetchStatistics({ data }: { type: string; data: FetchStatisticsPayload }) {
  try {
    const range = data.range || "7d";
    const cacheKey = `${data.serviceTokenId || "global"}_${range}`;

    // Skip if already cached
    const existing: StatisticsType | undefined = yield select(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (state: any) => state.infra?.statistics?.cache?.[cacheKey],
    );
    if (existing) {
      return;
    }

    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - (RANGE_DAYS[range] ?? 7));

    const formatDate = (d: Date) => d.toISOString().replace(/\.\d{3}Z$/, "Z");

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = {
      start_at: formatDate(startDate),
      end_at: formatDate(endDate),
      unsafe_count: 1000,
      error_count: 1000,
    };
    if (data.serviceTokenId) {
      params.service_token_id = data.serviceTokenId;
    }

    const response: InternalResponse<StatisticsType> = yield call(fetchStatisticsApi, {
      params,
      token: data.token,
    });

    yield put({
      type: actions.FETCHED_STATISTICS,
      data: {
        key: cacheKey,
        data: response.success ? response.data : null,
      },
    });
  } catch (error) {
    const range = data.range || "7d";
    const cacheKey = `${data.serviceTokenId || "global"}_${range}`;
    yield put({
      type: actions.FETCHED_STATISTICS,
      data: { key: cacheKey, data: null },
    });
  }
}

export default function* infraSaga(): Generator<ForkEffect<never>, void, unknown> {
  yield takeLatest(actions.FETCH_SERVICE_TOKENS, handleFetchServiceTokens);
  yield takeEvery(actions.FETCH_STATISTICS, handleFetchStatistics);
}
